import { motion } from "framer-motion";
import { Zap, Mail, ArrowUpRight } from "lucide-react";
import { trackShare } from "../analytics";

const XIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.742l7.736-8.83L1.254 2.25H8.08l4.253 5.622zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const RedditIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 20 20" fill="currentColor" className={className}>
    <path d="M20 10.002a10 10 0 10-10 10 10 10 0 0010-10zM3.33 10a1.24 1.24 0 012.18-.82 6.07 6.07 0 013.31-.92l.56-2.64-1.83.38a.88.88 0 11-.08-.44l2.03-.43a.22.22 0 01.26.17l.62 2.94a6.1 6.1 0 013.35.92 1.24 1.24 0 11-1.44 1.96.68.68 0 01-.02.17c0 1.66-1.93 3-4.32 3S3.51 12.83 3.51 11.17a.68.68 0 010-.17A1.24 1.24 0 013.33 10zm6.69 2.64c.67 0 1.21-.3 1.21-.67s-.54-.67-1.21-.67-1.21.3-1.21.67.54.67 1.21.67zm-2.97-.9a.73.73 0 101.46 0 .73.73 0 00-1.46 0zm4.61.73a.73.73 0 100-1.46.73.73 0 000 1.46z" />
  </svg>
);

const shareText = encodeURIComponent(
  "Just converted my OpenAPI spec to an MCP server in seconds with @apitomcp 🔥 No code needed — works with Claude, Cursor & Cline instantly. Try it free:"
);
const shareUrl  = encodeURIComponent("https://apitomcpconverter.com");
const twitterUrl = `https://twitter.com/intent/tweet?text=${shareText}&url=${shareUrl}`;
const redditUrl  = `https://www.reddit.com/submit?url=${shareUrl}&title=Convert%20any%20API%20to%20MCP%20in%20seconds%20%E2%80%94%20free%20tool`;

const LINKS = [
  { label: "Product",  items: [
    { name: "Convert API",  href: "/" },
    { name: "Registry",     href: "/registry" },
    { name: "Pricing",      href: "/pricing" },
    { name: "Dashboard",    href: "/dashboard" },
  ]},
  { label: "Popular MCPs", items: [
    { name: "Notion MCP",   href: "/mcp/notion-mcp" },
    { name: "Linear MCP",   href: "/mcp/linear-mcp" },
    { name: "Coinbase MCP", href: "/mcp/coinbase-mcp" },
    { name: "PokéAPI MCP",  href: "/mcp/pokeapi-mcp" },
  ]},
  { label: "Guides", items: [
    { name: "Blog",                   href: "/blog" },
    { name: "OpenAPI to MCP",         href: "/guides/openapi-to-mcp" },
    { name: "Claude Desktop Setup",   href: "/guides/claude-desktop-mcp-setup" },
    { name: "OAuth MCP Setup",        href: "/guides/oauth-mcp-setup" },
  ]},
  { label: "Legal", items: [
    { name: "Privacy",       href: "/privacy" },
    { name: "Terms",         href: "/terms" },
    { name: "Refund Policy", href: "/refund-policy" },
    { name: "System Status", href: "/status" },
  ]},
  { label: "Connect", items: [
    { name: "Share on X",      href: twitterUrl, external: true },
    { name: "Share on Reddit", href: redditUrl,  external: true },
    { name: "Send feedback",   href: "mailto:sufyameher@gmail.com?subject=Feedback", external: true },
  ]},
];

const Footer = () => {
  const year = new Date().getFullYear();

  return (
    <footer className="border-t border-border/40 bg-background">

      {/* ── Main footer ── */}
      <div className="max-w-6xl mx-auto px-6 py-16">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-10">

          {/* Brand column */}
          <div className="col-span-2 md:col-span-3 lg:col-span-2">
            <div className="flex items-center gap-2.5 mb-4">
              <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
                <Zap className="w-4 h-4 text-white" />
              </div>
              <span className="font-bold text-foreground text-base tracking-tight">API → MCP</span>
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed max-w-[200px]">
              Convert any API spec to a live MCP server in seconds. No code required.
            </p>
            <div className="flex items-center gap-2 mt-5">
              <motion.a
                href={twitterUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackShare("twitter")}
                whileHover={{ scale: 1.08 }}
                whileTap={{ scale: 0.95 }}
                className="w-8 h-8 rounded-lg border border-border flex items-center justify-center text-muted-foreground hover:text-foreground hover:border-foreground/30 transition-colors"
                aria-label="Share on X"
              >
                <XIcon className="w-3.5 h-3.5" />
              </motion.a>
              <motion.a
                href={redditUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackShare("reddit")}
                whileHover={{ scale: 1.08 }}
                whileTap={{ scale: 0.95 }}
                className="w-8 h-8 rounded-lg border border-border flex items-center justify-center text-muted-foreground hover:text-orange-500 hover:border-orange-300 transition-colors"
                aria-label="Share on Reddit"
              >
                <RedditIcon className="w-3.5 h-3.5" />
              </motion.a>
              <motion.a
                href="mailto:sufyameher@gmail.com?subject=Feedback"
                whileHover={{ scale: 1.08 }}
                whileTap={{ scale: 0.95 }}
                className="w-8 h-8 rounded-lg border border-border flex items-center justify-center text-muted-foreground hover:text-foreground hover:border-foreground/30 transition-colors"
                aria-label="Email feedback"
              >
                <Mail className="w-3.5 h-3.5" />
              </motion.a>
            </div>
          </div>

          {/* Link columns */}
          {LINKS.map(col => (
            <div key={col.label}>
              <p className="text-xs font-semibold text-foreground uppercase tracking-widest mb-4">
                {col.label}
              </p>
              <ul className="space-y-3">
                {col.items.map(item => (
                  <li key={item.name}>
                    <a
                      href={item.href}
                      target={(item as { external?: boolean }).external ? "_blank" : undefined}
                      rel={(item as { external?: boolean }).external ? "noopener noreferrer" : undefined}
                      className="group inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
                    >
                      {item.name}
                      {(item as { external?: boolean }).external && (
                        <ArrowUpRight className="w-3 h-3 opacity-0 group-hover:opacity-60 transition-opacity" />
                      )}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      {/* ── Bottom bar ── */}
      <div className="border-t border-border/40">
        <div className="max-w-6xl mx-auto px-6 py-5 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs text-muted-foreground">
            © {year} apitomcpconverter.com — All rights reserved.
          </p>
          <p className="text-xs text-muted-foreground">
            Built for developers, by developers.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
